import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  Circle,
} from "lucide-react"
import { useEffect, useRef } from "react"
import { useEditor, useEditorState, EditorContent } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import { TextStyle } from "@tiptap/extension-text-style"
import Color from "@tiptap/extension-color"
import Placeholder from "@tiptap/extension-placeholder"
import Collaboration from "@tiptap/extension-collaboration"
import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/lib/auth"
import { entryStateKey } from "@/lib/vocabulary-entries"
import { OUTCOME_FOR_STATE } from "@/lib/live/captures-log"
import { VocabularyEntryOutcomeEnum } from "@/__generated__/graphql"
import { useClassroomDoc } from "./LiveDocProvider"
import { WordPopover } from "./WordPopover"
import { VocabularyStrip } from "./VocabularyStrip"
import { PracticeCaptureNotice } from "./PracticeCaptureNotice"
import { CaptureMark } from "./capture-mark"
import { useLessonCaptures } from "./useLessonCaptures"
import { useLessonNotesAutosave } from "./useLessonNotesAutosave"

interface TextEditorProps {
  className?: string
}

// Text-color swatches for the toolbar. `null` is "default" — it unsets any color
// mark so the text inherits the editor's foreground. `key` is the locale leaf
// under liveSession.textEditor.colors.*.
const TEXT_COLORS: Array<{ key: string; value: string | null }> = [
  { key: "default", value: null },
  { key: "red", value: "#ef4444" },
  { key: "blue", value: "#3b82f6" },
  { key: "green", value: "#22c55e" },
  { key: "orange", value: "#f97316" },
  { key: "purple", value: "#a855f7" },
]

// Collaborative lesson notes: a TipTap (ProseMirror) rich-text editor bound to the
// session's shared Y.Doc via the Collaboration extension. Edits flow into the doc's
// `notes` XML fragment and ride the existing Daily/Yjs transport to the peer — no
// bespoke text-diffing needed; the CRDT handles concurrent edits and IME. Selecting
// text pops a word-lookup card (reading + definition + pronounce + "make flashcard",
// see WordPopover); captured words are marked in the text and listed in the
// "Vocabulary this lesson" strip above the notes (#825 phase 2). Outside a live
// session it degrades to a local-only notepad (no lesson: no popover, no strip).
export function TextEditor({ className }: TextEditorProps) {
  const { t, i18n } = useTranslation()
  const { user } = useAuth()
  const { doc, lessonId, enabled, targetLanguageCode, practice, notesReady } = useClassroomDoc()

  // Capture needs a lesson, not the collab flag: the mutation, the strip and the
  // marker all work from the local doc + the subscription + hydration. Only
  // replication to the peer needs the transport, which LiveDocProvider attaches
  // when collab is on.
  const canLookUp = Boolean(lessonId)

  // `useTranslation`'s `t` is a fixed-language snapshot (`getFixedT(currentLanguage)`
  // under the hood) — it does NOT track later language changes. The extension
  // closures below are captured once, when `useEditor` (re)builds them, so they'd
  // otherwise freeze on whatever locale was live at that moment (e.g. the SSR
  // default "en", before LocaleSync switches the real locale post-mount). Read
  // `t` through a ref so the closures always call the current translator.
  const tRef = useRef(t)
  tRef.current = t

  const editor = useEditor(
    {
      // The marketing "How it works" demo server-renders this component; TipTap must
      // not touch the DOM during SSR. It mounts client-side after hydration.
      immediatelyRender: false,
      extensions: [
        // Collaboration owns shared undo/redo (yUndoPlugin); StarterKit's own
        // history would fight it, so disable it.
        StarterKit.configure({ undoRedo: false }),
        TextStyle,
        Color,
        // A function, called on every render of the placeholder decoration, that
        // reads `t` through the ref so a later language switch is picked up
        // without waiting for `useEditor` to rebuild the extension.
        Placeholder.configure({ placeholder: () => tRef.current("liveSession.textEditor.placeholder") }),
        Collaboration.configure({ document: doc, field: "notes" }),
        // After Collaboration: the capture marker replicates through the notes
        // fragment like bold. The hover label is rendered per viewer. A state
        // outside the known enum (e.g. pasted HTML carrying a hand-edited
        // data-capture-state) falls back to "queued" rather than throwing inside
        // ProseMirror's render.
        CaptureMark.configure({
          labelFor: (state) => tRef.current(entryStateKey(OUTCOME_FOR_STATE[state] ?? VocabularyEntryOutcomeEnum.Queued)),
        }),
      ],
      editorProps: {
        attributes: {
          class: "min-h-full p-6 outline-hidden font-mono text-sm leading-relaxed",
        },
      },
    },
    // `i18n.language` forces the editor (and its extensions) to be rebuilt on a
    // language switch, so already-painted decorations (the placeholder) and mark
    // titles (the capture hover label) repaint in the new locale. Recreating is
    // lossless: the content lives in the Y.Doc and Collaboration re-binds to the
    // same fragment.
    [doc, i18n.language],
  )

  // The lesson's captures: rows for the strip, the subscription + hydration, the
  // marks kept in step, undo / retry / keep my spelling.
  const captures = useLessonCaptures({ doc, lessonId, editor, viewerRole: user?.role })

  // Autosave of the plain-text notes to the lesson (#42, #1033): debounced, and
  // flushed when the editor or the page goes away.
  // Nothing is sent before the classroom has decided whether to restore the
  // saved notes (#1034): an empty editor must never overwrite them.
  useLessonNotesAutosave(editor, lessonId, enabled && notesReady)

  // Read-only until then, so nobody types into notes that are about to appear.
  useEffect(() => {
    editor?.setEditable(notesReady)
  }, [editor, notesReady])

  // Reactive view of the editor for the toolbar. TipTap v3's useEditor does not
  // re-render on every transaction, so derive the active marks the toolbar needs
  // through useEditorState.
  const state = useEditorState({
    editor,
    selector: ({ editor }) => {
      if (!editor) {
        return { isBold: false, isItalic: false, isUnderline: false, color: null as string | null }
      }
      return {
        isBold: editor.isActive("bold"),
        isItalic: editor.isActive("italic"),
        isUnderline: editor.isActive("underline"),
        color: (editor.getAttributes("textStyle").color as string | undefined) ?? null,
      }
    },
  })
  const { isBold, isItalic, isUnderline, color } = state ?? {
    isBold: false,
    isItalic: false,
    isUnderline: false,
    color: null,
  }

  return (
    <div className={cn("flex flex-col h-full", className)}>
      {/* Persistent toolbar — formatting controls. `onMouseDown` preventDefault keeps
          the editor selection alive when a control is pressed (a blur would otherwise
          drop it before the command runs). */}
      <div className="flex flex-wrap items-center gap-1 px-3 py-2 border-b border-border bg-card shrink-0">
        <Button
          variant={isBold ? "default" : "ghost"}
          size="sm"
          disabled={!editor}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor?.chain().focus().toggleBold().run()}
          aria-label={t("liveSession.textEditor.bold")}
        >
          <BoldIcon className="w-4 h-4" />
        </Button>
        <Button
          variant={isItalic ? "default" : "ghost"}
          size="sm"
          disabled={!editor}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
          aria-label={t("liveSession.textEditor.italic")}
        >
          <ItalicIcon className="w-4 h-4" />
        </Button>
        <Button
          variant={isUnderline ? "default" : "ghost"}
          size="sm"
          disabled={!editor}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => editor?.chain().focus().toggleUnderline().run()}
          aria-label={t("liveSession.textEditor.underline")}
        >
          <UnderlineIcon className="w-4 h-4" />
        </Button>

        <div className="w-px h-6 bg-border mx-1" />

        {TEXT_COLORS.map((c) => {
          const active = c.value ? color === c.value : !color
          return (
            <button
              key={c.key}
              type="button"
              disabled={!editor}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (!editor) return
                if (c.value) editor.chain().focus().setColor(c.value).run()
                else editor.chain().focus().unsetColor().run()
              }}
              className={cn(
                "focus-ring p-1 rounded-md transition-all disabled:opacity-50",
                active && "ring-2 ring-primary ring-offset-1",
              )}
              aria-label={t("liveSession.textEditor.colorText", { color: t(`liveSession.textEditor.colors.${c.key}`) })}
            >
              <Circle className="w-5 h-5" fill={c.value ?? "currentColor"} stroke={c.value ?? "currentColor"} />
            </button>
          )
        })}
      </div>

      {/* The practice classroom (#945): show WHERE capture lives, disabled — a
          real lesson renders the live strip/popover below instead. */}
      {practice && !lessonId && <PracticeCaptureNotice />}

      {lessonId && (
        <VocabularyStrip
          rows={captures.rows}
          viewerRole={user?.role}
          onUndo={captures.undo}
          onRetry={captures.retry}
          onKeepSpelling={captures.keepSpelling}
          restoringIds={captures.restoringIds}
        />
      )}

      {canLookUp && lessonId && (
        <WordPopover
          editor={editor}
          lessonId={lessonId}
          targetLanguageCode={targetLanguageCode}
          onCaptured={captures.recordEntry}
          onUndo={captures.undo}
        />
      )}

      <div className="relative flex-1 overflow-y-auto min-h-0">
        <EditorContent editor={editor} className="h-full" />
        {!notesReady && (
          // Covers the editor's own placeholder, which sits in the same spot.
          <p role="status" className="absolute inset-x-0 top-0 bg-background p-6 font-mono text-sm leading-relaxed text-muted-foreground">
            {t("liveSession.textEditor.loadingNotes")}
          </p>
        )}
      </div>
    </div>
  )
}
